/**
 * External store between the annotation controller and the per-page overlays. Overlays subscribe to
 * THEIR page only, so a change re-renders just the page(s) whose objects changed — the engine's
 * objectsByPage may be rebuilt on every edit; unchanged pages keep their previous array identity.
 * Transient UI (selection, drag preview, eraser marks, connector draft) lives here too, and the
 * wet-stroke elements are registered here so pointer handlers can draw without React.
 */
import type { AnnotationObject } from '../../shared/annotations';

export interface DragPreview {
  id: string;
  page: number;
  /** Translation, normalised page units. */
  dx: number;
  dy: number;
  /** Scale about (ox, oy), normalised. */
  k: number;
  ox: number;
  oy: number;
}

export interface PageMark {
  page: number;
  x: number;
  y: number;
}

export interface OverlayUi {
  editing: boolean;
  selectedId: string | null;
  preview: DragPreview | null;
  /** Objects touched by the eraser in the current drag (drawn faded), and the pages they are on. */
  erasing: ReadonlySet<string>;
  erasingPages: ReadonlySet<number>;
  /** Source of a connector being created (shown as a pin of the Tengill size). */
  draft: (PageMark & { color: string; size: number }) | null;
  /** Target of the selected connector (shown as a target marker). */
  target: PageMark | null;
  /** Object hidden while it is edited in the text box. */
  hiddenId: string | null;
  /** A finger may drag the selection (its handles block scrolling); else a finger scrolls over it. */
  grab: boolean;
  /** The selection shows its resize handle and move grip (routing.ts selectionScalable). */
  scalable: boolean;
}

export interface WetElements {
  ink: SVGPathElement | null;
  highlight: SVGLineElement | null;
}

/** The part of OverlayUi one page needs (unchanged object identity while nothing on it changed). */
export interface PageUi {
  editing: boolean;
  selectedId: string | null;
  hiddenId: string | null;
  grab: boolean;
  scalable: boolean;
  preview: DragPreview | null;
  erasing: ReadonlySet<string>;
  draft: OverlayUi['draft'];
  target: PageMark | null;
}

const EMPTY: readonly AnnotationObject[] = [];
const NO_IDS: ReadonlySet<string> = new Set();
const NO_PAGES: ReadonlySet<number> = new Set();

export const INITIAL_UI: OverlayUi = {
  editing: false,
  selectedId: null,
  preview: null,
  erasing: NO_IDS,
  erasingPages: NO_PAGES,
  draft: null,
  target: null,
  hiddenId: null,
  grab: true,
  scalable: false,
};

function samePageUi(a: PageUi, b: PageUi): boolean {
  return (
    a.editing === b.editing &&
    a.selectedId === b.selectedId &&
    a.hiddenId === b.hiddenId &&
    a.grab === b.grab &&
    a.scalable === b.scalable &&
    a.preview === b.preview &&
    a.erasing === b.erasing &&
    a.draft === b.draft &&
    a.target === b.target
  );
}

function samePage(a: readonly AnnotationObject[], b: readonly AnnotationObject[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (x !== y && (x.id !== y.id || x.hlcMs !== y.hlcMs || x.deviceId !== y.deviceId || x.page !== y.page)) return false;
  }
  return true;
}

export class OverlayStore {
  private pages = new Map<number, readonly AnnotationObject[]>();
  private pageListeners = new Map<number, Set<() => void>>();
  private uiListeners = new Set<() => void>();
  private uiState: OverlayUi = INITIAL_UI;
  readonly wet = new Map<number, WetElements>();
  /** Committed stroke whose wet preview stays until the overlay shows the real object. */
  pendingWet: { page: number; id: string; timer: ReturnType<typeof setTimeout> } | null = null;

  /* objects */

  setObjects(byPage: ReadonlyMap<number, readonly AnnotationObject[]>): void {
    const changed: number[] = [];
    for (const [page, list] of byPage) {
      const prev = this.pages.get(page);
      if (prev && samePage(prev, list)) continue;
      this.pages.set(page, list);
      changed.push(page);
    }
    for (const page of [...this.pages.keys()]) {
      if (!byPage.has(page)) {
        this.pages.delete(page);
        changed.push(page);
      }
    }
    for (const page of changed) this.pageListeners.get(page)?.forEach((cb) => cb());
  }

  getPage(page: number): readonly AnnotationObject[] {
    return this.pages.get(page) ?? EMPTY;
  }

  subscribePage(page: number, cb: () => void): () => void {
    let set = this.pageListeners.get(page);
    if (!set) {
      set = new Set();
      this.pageListeners.set(page, set);
    }
    set.add(cb);
    return () => {
      set.delete(cb);
    };
  }

  /* ui */

  get ui(): OverlayUi {
    return this.uiState;
  }

  setUi(patch: Partial<OverlayUi>): void {
    let changed = false;
    for (const k of Object.keys(patch) as (keyof OverlayUi)[]) {
      if (this.uiState[k] !== patch[k]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    this.uiState = { ...this.uiState, ...patch };
    this.uiListeners.forEach((cb) => cb());
  }

  subscribeUi = (cb: () => void): (() => void) => {
    this.uiListeners.add(cb);
    return () => {
      this.uiListeners.delete(cb);
    };
  };

  getUi = (): OverlayUi => this.uiState;

  private pageUiCache = new Map<number, PageUi>();

  /** UI slice for one page; a drag preview or eraser fade on another page leaves it untouched. */
  getPageUi(page: number): PageUi {
    const u = this.uiState;
    const next: PageUi = {
      editing: u.editing,
      selectedId: u.selectedId,
      hiddenId: u.hiddenId,
      grab: u.grab,
      scalable: u.scalable,
      preview: u.preview && u.preview.page === page ? u.preview : null,
      erasing: u.erasingPages.has(page) ? u.erasing : NO_IDS,
      draft: u.draft && u.draft.page === page ? u.draft : null,
      target: u.target && u.target.page === page ? u.target : null,
    };
    const prev = this.pageUiCache.get(page);
    if (prev && samePageUi(prev, next)) return prev;
    this.pageUiCache.set(page, next);
    return next;
  }

  /* wet strokes */

  registerWet(page: number, els: WetElements | null): void {
    if (els) this.wet.set(page, els);
    else this.wet.delete(page);
  }

  clearWet(page: number): void {
    const w = this.wet.get(page);
    if (!w) return;
    if (w.ink) {
      w.ink.setAttribute('d', '');
    }
    if (w.highlight) w.highlight.setAttribute('visibility', 'hidden');
  }

  /** Keep the wet stroke until the object shows up on its page (or 1.5 s at most). */
  holdWet(page: number, id: string): void {
    this.releaseWet();
    const timer = setTimeout(() => this.releaseWet(), 1500);
    this.pendingWet = { page, id, timer };
  }

  releaseWet(): void {
    const p = this.pendingWet;
    if (!p) return;
    clearTimeout(p.timer);
    this.pendingWet = null;
    this.clearWet(p.page);
  }

  /* settling: keep a move/scale preview or eraser fade until the committed change shows up */

  settling: { what: 'preview' | 'erase'; page: number; hlc: Map<string, number>; timer: ReturnType<typeof setTimeout> } | null = null;

  /** `hlc` = id → hlcMs before the change; the transient UI ends when every id changed or vanished. */
  settle(what: 'preview' | 'erase', page: number, hlc: Map<string, number>): void {
    this.endSettle();
    const timer = setTimeout(() => this.endSettle(), 1500);
    this.settling = { what, page, hlc, timer };
  }

  checkSettled(page: number, objects: readonly AnnotationObject[]): void {
    const s = this.settling;
    if (!s || s.page !== page) return;
    for (const [id, hlc] of s.hlc) {
      const o = objects.find((x) => x.id === id);
      if (o && o.hlcMs === hlc) return;
    }
    this.endSettle();
  }

  endSettle(): void {
    const s = this.settling;
    if (!s) return;
    clearTimeout(s.timer);
    this.settling = null;
    this.setUi(s.what === 'preview' ? { preview: null } : { erasing: NO_IDS, erasingPages: NO_PAGES });
  }

  clearErasing(): void {
    this.setUi({ erasing: NO_IDS, erasingPages: NO_PAGES });
  }
}
